import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit, Eye, Upload, Image, Monitor, Smartphone, Plus, X, GripVertical } from 'lucide-react';

type Banner = {
    id: string;
    title: string;
    description?: string;
    image_url_desktop: string;
    image_url_mobile: string;
    link_url?: string;
    is_active: boolean;
    display_order: number;
    start_date?: string;
    end_date?: string;
    created_at: string;
    updated_at: string;
};

const BannersManagementSection: React.FC = () => {
    const [banners, setBanners] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
    const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
    const [uploadingDesktop, setUploadingDesktop] = useState(false);
    const [uploadingMobile, setUploadingMobile] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [bannerToDelete, setBannerToDelete] = useState<Banner | null>(null);

    const desktopInputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image_url_desktop: '',
        image_url_mobile: '',
        link_url: '',
        is_active: true,
        start_date: '',
        end_date: '',
    });

    // Carregar banners
    const loadBanners = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('dashboard_banners')
                .select('*')
                .order('display_order', { ascending: true });

            if (error) throw error;
            setBanners(data || []);
        } catch (err) {
            console.error('Erro ao carregar banners:', err);
            setMessage('Erro ao carregar banners: ' + (err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBanners();
    }, []);

    // Upload de imagem para o Supabase Storage
    const uploadImage = async (file: File, type: 'desktop' | 'mobile'): Promise<string | null> => {
        try {
            if (type === 'desktop') setUploadingDesktop(true);
            else setUploadingMobile(true);

            const fileExt = file.name.split('.').pop();
            const fileName = `${type}-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `banners/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('dashboard-banners')
                .upload(filePath, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) {
                // Se o bucket não existir, tentar criar ou usar outro bucket
                console.error('Erro no upload:', uploadError);

                // Fallback: usar URL de dados em base64 temporariamente
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target?.result as string);
                    reader.readAsDataURL(file);
                });
            }

            const { data: { publicUrl } } = supabase.storage
                .from('dashboard-banners')
                .getPublicUrl(filePath);

            return publicUrl;
        } catch (error) {
            console.error('Erro ao fazer upload:', error);
            return null;
        } finally {
            if (type === 'desktop') setUploadingDesktop(false);
            else setUploadingMobile(false);
        }
    };

    // Handler de seleção de arquivo
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>, type: 'desktop' | 'mobile') => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setMessage('Por favor, selecione apenas arquivos de imagem');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setMessage('A imagem deve ter no máximo 5MB');
            return;
        }

        const url = await uploadImage(file, type);
        if (url) {
            setFormData(prev => ({
                ...prev,
                [type === 'desktop' ? 'image_url_desktop' : 'image_url_mobile']: url
            }));
            setMessage(`Imagem ${type} carregada com sucesso!`);
            setTimeout(() => setMessage(''), 3000);
        }
    };

    // Submeter formulário
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.image_url_desktop || !formData.image_url_mobile) {
            setMessage('Por favor, faça upload das imagens desktop e mobile');
            return;
        }

        try {
            setLoading(true);

            const bannerData = {
                title: formData.title || null,
                description: formData.description || null,
                image_url_desktop: formData.image_url_desktop,
                image_url_mobile: formData.image_url_mobile,
                link_url: formData.link_url || null,
                is_active: formData.is_active,
                start_date: formData.start_date || null,
                end_date: formData.end_date || null,
                display_order: editingBanner ? editingBanner.display_order : (banners.length + 1),
            };

            if (editingBanner) {
                const { error } = await supabase
                    .from('dashboard_banners')
                    .update(bannerData)
                    .eq('id', editingBanner.id);

                if (error) throw error;
                setMessage('Banner atualizado com sucesso!');
            } else {
                const { error } = await supabase
                    .from('dashboard_banners')
                    .insert([bannerData]);

                if (error) throw error;
                setMessage('Banner criado com sucesso!');
            }

            setIsModalOpen(false);
            resetForm();
            loadBanners();
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error('Erro ao salvar banner:', err);
            setMessage('Erro ao salvar banner: ' + (err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    // Resetar formulário
    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            image_url_desktop: '',
            image_url_mobile: '',
            link_url: '',
            is_active: true,
            start_date: '',
            end_date: '',
        });
        setEditingBanner(null);
    };

    // Editar banner
    const handleEdit = (banner: Banner) => {
        setEditingBanner(banner);
        setFormData({
            title: banner.title || '',
            description: banner.description || '',
            image_url_desktop: banner.image_url_desktop,
            image_url_mobile: banner.image_url_mobile,
            link_url: banner.link_url || '',
            is_active: banner.is_active,
            start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
            end_date: banner.end_date ? banner.end_date.split('T')[0] : '',
        });
        setIsModalOpen(true);
    };

    // Abrir modal de confirmação de exclusão
    const openDeleteModal = (banner: Banner) => {
        setBannerToDelete(banner);
        setIsDeleteModalOpen(true);
    };

    // Confirmar exclusão do banner
    const confirmDelete = async () => {
        if (!bannerToDelete) return;

        try {
            setLoading(true);
            const { error } = await supabase
                .from('dashboard_banners')
                .delete()
                .eq('id', bannerToDelete.id);

            if (error) throw error;
            setMessage('Banner excluído com sucesso!');
            loadBanners();
            setTimeout(() => setMessage(''), 3000);
        } catch (err) {
            console.error('Erro ao excluir banner:', err);
            setMessage('Erro ao excluir banner: ' + (err as Error).message);
        } finally {
            setLoading(false);
            setIsDeleteModalOpen(false);
            setBannerToDelete(null);
        }
    };

    // Toggle ativo/inativo
    const handleToggleActive = async (banner: Banner) => {
        try {
            const { error } = await supabase
                .from('dashboard_banners')
                .update({ is_active: !banner.is_active })
                .eq('id', banner.id);

            if (error) throw error;
            loadBanners();
        } catch (err) {
            console.error('Erro ao atualizar status:', err);
            setMessage('Erro ao atualizar status');
        }
    };

    // Preview do banner
    const handlePreview = (banner: Banner) => {
        setPreviewBanner(banner);
        setIsPreviewOpen(true);
    };

    return (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-8">
            <div className="mb-6">
                <p className="text-gray-600">
                    Gerencie os banners promocionais que aparecem no dashboard de todos os usuários.
                    Faça upload de imagens separadas para desktop e mobile.
                </p>
            </div>

            {/* Botão Novo Banner */}
            <div className="mb-4">
                <Button
                    onClick={() => {
                        resetForm();
                        setIsModalOpen(true);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Banner
                </Button>
            </div>

            {/* Mensagem de feedback */}
            {message && (
                <div className={`p-3 rounded-md text-sm mb-4 ${message.toLowerCase().includes('sucesso')
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                    {message}
                </div>
            )}

            {/* Loading */}
            {loading && banners.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    Carregando banners...
                </div>
            )}

            {/* Grid de Banners (Miniaturas) */}
            {banners.length === 0 && !loading ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <Image className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-2">Nenhum banner cadastrado</p>
                    <p className="text-sm text-gray-400">Clique em "Novo Banner" para adicionar</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {banners.map((banner) => (
                        <div
                            key={banner.id}
                            className={`relative group rounded-lg overflow-hidden border-2 transition-all duration-200 ${banner.is_active
                                ? 'border-green-200 hover:border-green-400'
                                : 'border-gray-200 hover:border-gray-400 opacity-60'
                                }`}
                        >
                            {/* Miniatura do Banner */}
                            <div
                                className="aspect-video bg-gray-100 cursor-pointer relative overflow-hidden"
                                onClick={() => handlePreview(banner)}
                            >
                                <img
                                    src={banner.image_url_desktop}
                                    alt={banner.title || 'Banner'}
                                    className="w-full h-full object-cover"
                                />

                                {/* Overlay no hover */}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                    <Eye className="w-8 h-8 text-white" />
                                </div>

                                {/* Badge de status */}
                                <div className="absolute top-2 right-2">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${banner.is_active
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-500 text-white'
                                        }`}>
                                        {banner.is_active ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>

                                {/* Badge de ordem */}
                                <div className="absolute top-2 left-2">
                                    <span className="w-6 h-6 rounded-full bg-black/70 text-white text-xs font-bold flex items-center justify-center">
                                        {banner.display_order}
                                    </span>
                                </div>
                            </div>

                            {/* Informações e Ações */}
                            <div className="p-3 bg-white">
                                <h4 className="font-medium text-sm text-gray-900 truncate">
                                    {banner.title || 'Sem título'}
                                </h4>

                                <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleEdit(banner)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title="Editar"
                                        >
                                            <Edit className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handlePreview(banner)}
                                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                            title="Visualizar"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => openDeleteModal(banner)}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Excluir"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    <Switch
                                        checked={banner.is_active}
                                        onCheckedChange={() => handleToggleActive(banner)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de Criação/Edição */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingBanner ? 'Editar Banner' : 'Novo Banner'}
                        </DialogTitle>
                        <DialogDescription>
                            Faça upload de imagens separadas para desktop (1200x300px recomendado) e mobile (600x400px recomendado).
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Upload Desktop */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Monitor className="w-4 h-4" />
                                Imagem Desktop (1200x300px)
                            </Label>
                            <div className="flex items-center gap-4">
                                <input
                                    ref={desktopInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileSelect(e, 'desktop')}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => desktopInputRef.current?.click()}
                                    disabled={uploadingDesktop}
                                    className="flex-shrink-0"
                                >
                                    {uploadingDesktop ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Upload Desktop
                                        </>
                                    )}
                                </Button>
                                {formData.image_url_desktop && (
                                    <div className="relative group">
                                        <img
                                            src={formData.image_url_desktop}
                                            alt="Preview desktop"
                                            className="h-16 rounded border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, image_url_desktop: '' }))}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Upload Mobile */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Smartphone className="w-4 h-4" />
                                Imagem Mobile (600x400px)
                            </Label>
                            <div className="flex items-center gap-4">
                                <input
                                    ref={mobileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleFileSelect(e, 'mobile')}
                                    className="hidden"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => mobileInputRef.current?.click()}
                                    disabled={uploadingMobile}
                                    className="flex-shrink-0"
                                >
                                    {uploadingMobile ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-2" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Upload Mobile
                                        </>
                                    )}
                                </Button>
                                {formData.image_url_mobile && (
                                    <div className="relative group">
                                        <img
                                            src={formData.image_url_mobile}
                                            alt="Preview mobile"
                                            className="h-16 rounded border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, image_url_mobile: '' }))}
                                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Título */}
                        <div className="space-y-2">
                            <Label htmlFor="title">Título (opcional)</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Ex: Promoção de Verão"
                            />
                        </div>

                        {/* Descrição */}
                        <div className="space-y-2">
                            <Label htmlFor="description">Descrição (opcional)</Label>
                            <Input
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Descrição do banner"
                            />
                        </div>

                        {/* Link URL */}
                        <div className="space-y-2">
                            <Label htmlFor="link_url">Link de destino (opcional)</Label>
                            <Input
                                id="link_url"
                                type="url"
                                value={formData.link_url}
                                onChange={(e) => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                                placeholder="https://exemplo.com"
                            />
                        </div>

                        {/* Datas */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="start_date">Data início (opcional)</Label>
                                <Input
                                    id="start_date"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="end_date">Data fim (opcional)</Label>
                                <Input
                                    id="end_date"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                />
                            </div>
                        </div>

                        {/* Status Ativo */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label>Banner Ativo</Label>
                                <p className="text-xs text-gray-500">Banners inativos não serão exibidos</p>
                            </div>
                            <Switch
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                            />
                        </div>

                        {/* Botões */}
                        <div className="flex gap-3 pt-4">
                            <Button
                                type="submit"
                                className="flex-1 bg-green-600 hover:bg-green-700"
                                disabled={loading || uploadingDesktop || uploadingMobile}
                            >
                                {loading ? 'Salvando...' : (editingBanner ? 'Atualizar' : 'Criar Banner')}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    resetForm();
                                }}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal de Preview */}
            <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader className="space-y-4">
                        <DialogTitle className="pr-8">
                            Preview: {previewBanner?.title || 'Banner'}
                        </DialogTitle>
                        {/* Botões de toggle Desktop/Mobile - Linha separada */}
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant={previewMode === 'desktop' ? 'default' : 'outline'}
                                onClick={() => setPreviewMode('desktop')}
                                className="gap-1"
                            >
                                <Monitor className="w-4 h-4" />
                                Desktop
                            </Button>
                            <Button
                                size="sm"
                                variant={previewMode === 'mobile' ? 'default' : 'outline'}
                                onClick={() => setPreviewMode('mobile')}
                                className="gap-1"
                            >
                                <Smartphone className="w-4 h-4" />
                                Mobile
                            </Button>
                        </div>
                    </DialogHeader>

                    {previewBanner && (
                        <div className="flex justify-center bg-gray-100 p-4 rounded-lg">
                            {previewMode === 'desktop' ? (
                                <div className="w-full max-w-3xl">
                                    <img
                                        src={previewBanner.image_url_desktop}
                                        alt="Preview Desktop"
                                        className="w-full rounded-lg shadow-lg"
                                    />
                                </div>
                            ) : (
                                <div className="w-64 mx-auto">
                                    <div className="bg-gray-900 rounded-3xl p-2 shadow-xl">
                                        <img
                                            src={previewBanner.image_url_mobile}
                                            alt="Preview Mobile"
                                            className="w-full rounded-2xl"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {previewBanner && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-gray-500">Status:</span>
                                    <span className={`ml-2 font-medium ${previewBanner.is_active ? 'text-green-600' : 'text-gray-600'}`}>
                                        {previewBanner.is_active ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Ordem:</span>
                                    <span className="ml-2 font-medium">#{previewBanner.display_order}</span>
                                </div>
                                {previewBanner.link_url && (
                                    <div className="col-span-2">
                                        <span className="text-gray-500">Link:</span>
                                        <a href={previewBanner.link_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                                            {previewBanner.link_url}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal de Confirmação de Exclusão */}
            <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <Trash2 className="w-5 h-5" />
                            Confirmar Exclusão
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Tem certeza que deseja excluir o banner
                            <strong className="text-gray-900"> "{bannerToDelete?.title || 'Sem título'}"</strong>?
                            <br />
                            <span className="text-red-500 text-sm mt-2 block">
                                Esta ação não pode ser desfeita.
                            </span>
                        </DialogDescription>
                    </DialogHeader>

                    {bannerToDelete && (
                        <div className="my-4 rounded-lg overflow-hidden border">
                            <img
                                src={bannerToDelete.image_url_desktop}
                                alt={bannerToDelete.title || 'Banner'}
                                className="w-full h-24 object-cover"
                            />
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                                setIsDeleteModalOpen(false);
                                setBannerToDelete(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            onClick={confirmDelete}
                            disabled={loading}
                        >
                            {loading ? 'Excluindo...' : 'Excluir Banner'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default BannersManagementSection;
